import { Heading, VStack, HStack, Box, Flex, Text, Icon, Image } from "@chakra-ui/react";
import FullScreenSection from "./FullScreenSection";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { keyframes } from "@emotion/react";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const steps = [
  {
    number: "1",
    title: "Sign Up",
    description: "Create your Swiftchang account in minutes"
  },
  {
    number: "2",
    title: "Verify Identity",
    description: "Complete KYC verification for security"
  },
  {
    number: "3",
    title: "Choose Currencies",
    description: "Select the currencies you want in your wallet"
  },
  {
    number: "4",
    title: "Start Using",
    description: "Your multi-currency wallets are ready to use"
  }
];

const benefits = [
  {
    icon: CheckCircleIcon,
    color: "blue.500",
    title: "Hold Multiple Currencies",
    description: "Store CAD, NGN, USD, GBP and more in one place"
  },
  {
    icon: CheckCircleIcon,
    color: "green.500",
    title: "Zero Monthly Fees",
    description: "No maintenance charges or hidden costs"
  },
  {
    icon: CheckCircleIcon,
    color: "purple.500",
    title: "Instant Currency Swap",
    description: "Exchange between wallets at competitive rates"
  },
  {
    icon: CheckCircleIcon,
    color: "orange.500",
    title: "Real-time Balance",
    description: "Track all your currencies in one dashboard"
  },
  {
    icon: CheckCircleIcon,
    color: "teal.500",
    title: "Secure & Protected",
    description: "Bank-level encryption and fraud protection"
  },
  {
    icon: CheckCircleIcon,
    color: "pink.500",
    title: "24/7 Access",
    description: "Manage your money anytime, anywhere"
  }
];

const MultiCurrency = () => {
  return (
    <FullScreenSection
      justifyContent="center"
      alignItems="center"
      isDarkBackground={false}
      backgroundColor="white"
      py={100}
    >
      <VStack spacing={{ base: 10, md: 16 }} width="100%" maxW="1200px">
        {/* Header Section */}
        <VStack spacing={4} textAlign="center">
          <Heading fontSize={{ base: "2.2rem", md: "3rem", lg: "3.5rem" }} fontWeight="bold">
            Multi-Currency Wallet Accounts
          </Heading>
          <Text fontSize={{ base: "1rem", md: "1.1rem", lg: "1.3rem" }} color="gray.600" maxW="800px">
            Manage all your currencies in one place. Send, receive, and exchange between CAD, NGN, and other currencies effortlessly.
          </Text>
        </VStack>

        {/* Steps and Phone Section */}
        <Flex
          width="100%"
          gap={{ base: 8, md: 12 }}
          alignItems="center"
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
        >
          {/* Steps List */}
          <VStack spacing={{ base: 4, md: 6 }} flex="1" align="stretch">
            <Heading fontSize={{ base: "1.6rem", md: "1.8rem", lg: "2rem" }} fontWeight="bold" mb={4}>
              How to Get Started
            </Heading>
            {steps.map((step, index) => (
              <Flex
                key={index}
                direction={{ base: "column", sm: "row" }}
                align={{ base: "flex-start", sm: "center" }}
                gap={{ base: 3, sm: 4 }}
                p={{ base: 4, md: 6 }}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                border="1px solid"
                borderColor="gray.200"
                transition="all 0.3s ease"
                _hover={{ boxShadow: "xl", transform: "scale(1.02)", borderColor: "black" }}
              >
                <Box
                  minW={{ base: "44px", md: "60px" }}
                  minH={{ base: "44px", md: "60px" }}
                  borderRadius="full"
                  bg="black"
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: "lg", md: "2xl" }}
                  fontWeight="bold"
                >
                  {step.number}
                </Box>
                <VStack align="flex-start" spacing={1}>
                  <Heading fontSize={{ base: "1.1rem", md: "1.2rem", lg: "1.3rem" }} fontWeight="semibold">
                    {step.title}
                  </Heading>
                  <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>{step.description}</Text>
                </VStack>
              </Flex>
            ))}
          </VStack>

          {/* Phone Mockup
          <Box flex="1" display="flex" justifyContent="center">
            <Box
              position="relative"
              width="300px"
              height="600px"
              borderRadius="3xl"
              border="12px solid"
              borderColor="gray.800"
              bg="white"
              boxShadow="2xl"
              overflow="hidden"
            >
              {/* Phone Notch 
              <Box
                position="absolute"
                top="0"
                left="50%"
                transform="translateX(-50%)"
                width="150px"
                height="25px"
                bg="gray.800"
                borderBottomRadius="xl"
                zIndex="10"
              />

              {/* Phone Screen Content 
              <Box
                pt="30px"
                px={4}
                height="100%"
                bg="gradient"
                bgGradient="linear(to-b, blue.50, white)"
              >
                <VStack
                  spacing={6}
                  animation={`${fadeIn} 0.5s ease-in-out`}
                  pt={8}
                >
                  <Heading fontSize="xl" color="black">My Wallets</Heading>
                  <VStack spacing={4} width="100%">
                    {/* CAD Wallet 
                    <Box
                      width="100%"
                      p={5}
                      bg="white"
                      borderRadius="xl"
                      boxShadow="lg"
                      border="2px solid"
                      borderColor="blue.400"
                    >
                      <HStack justify="space-between" mb={3}>
                        <HStack>
                          <Image src="/flags/ca.svg" alt="Canada flag" boxSize="40px" />
                          <VStack align="flex-start" spacing={0}>
                            <Text fontWeight="bold" fontSize="lg">CAD Wallet</Text>
                            <Text fontSize="xs" color="gray.500">Canadian Dollar</Text>
                          </VStack>
                        </HStack>
                      </HStack>
                      <Text fontWeight="bold" fontSize="3xl" color="black">$5,000.00</Text>
                      <HStack mt={3} spacing={2}>
                        <Box
                          flex="1"
                          py={2}
                          bg="black"
                          borderRadius="md"
                          textAlign="center"
                        >
                          <Text color="white" fontSize="xs" fontWeight="bold">Send</Text>
                        </Box>
                        <Box
                          flex="1"
                          py={2}
                          bg="gray.100"
                          borderRadius="md"
                          textAlign="center"
                        >
                          <Text color="black" fontSize="xs" fontWeight="bold">Exchange</Text>
                        </Box>
                      </HStack>
                    </Box>

                    {/* NGN Wallet 
                    <Box
                      width="100%"
                      p={5}
                      bg="white"
                      borderRadius="xl"
                      boxShadow="lg"
                      border="2px solid"
                      borderColor="green.400"
                    >
                      <HStack justify="space-between" mb={3}>
                        <HStack>
                          <Image src="/flags/ng.svg" alt="Nigeria flag" boxSize="40px" />
                          <VStack align="flex-start" spacing={0}>
                            <Text fontWeight="bold" fontSize="lg">NGN Wallet</Text>
                            <Text fontSize="xs" color="gray.500">Nigerian Naira</Text>
                          </VStack>
                        </HStack>
                      </HStack>
                      <Text fontWeight="bold" fontSize="3xl" color="black">₦850,000.00</Text>
                      <HStack mt={3} spacing={2}>
                        <Box
                          flex="1"
                          py={2}
                          bg="black"
                          borderRadius="md"
                          textAlign="center"
                        >
                          <Text color="white" fontSize="xs" fontWeight="bold">Send</Text>
                        </Box>
                        <Box
                          flex="1"
                          py={2}
                          bg="gray.100"
                          borderRadius="md"
                          textAlign="center"
                        >
                          <Text color="black" fontSize="xs" fontWeight="bold">Exchange</Text>
                        </Box>
                      </HStack>
                    </Box>

                    {/* Add Wallet Button 
                    <Box
                      width="100%"
                      py={4}
                      bg="gray.50"
                      borderRadius="xl"
                      border="2px dashed"
                      borderColor="gray.300"
                      textAlign="center"
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ borderColor: "black", bg: "gray.100" }}
                    >
                      <Text color="gray.600" fontWeight="semibold" fontSize="sm">+ Add Currency</Text>
                    </Box>
                  </VStack>
                </VStack>
              </Box>
            </Box>
          </Box>  */}
        </Flex>

        {/* Benefits Section */}
        <VStack spacing={8} width="100%" mt={8}>
          <Heading fontSize="2.5rem" fontWeight="bold" textAlign="center">
            Why Choose Multi-Currency Wallets?
          </Heading>
          
          <Box width="100%">
            <Flex
              gap={8}
              flexWrap="wrap"
              justifyContent="center"
              width="100%"
            >
              {benefits.map((benefit, index) => (
                <VStack
                  key={index}
                  spacing={3}
                  flex="1"
                  minW="280px"
                  maxW="350px"
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  transition="all 0.3s ease"
                  _hover={{ boxShadow: "xl", transform: "translateY(-5px)" }}
                >
                  <Icon as={benefit.icon} boxSize={12} color={benefit.color} />
                  <Heading fontSize="1.2rem" fontWeight="semibold" textAlign="center">
                    {benefit.title}
                  </Heading>
                  <Text textAlign="center" color="gray.600">
                    {benefit.description}
                  </Text>
                </VStack>
              ))}
            </Flex>
          </Box>
        </VStack>
      </VStack>
    </FullScreenSection>
  );
};

export default MultiCurrency;
